import { NormalBlending, type Scene } from 'three';
import { VisualRandom } from '../../visual/index.js';
import type {
  QualityContext,
  VisualFieldReader,
  VisualFieldSample,
  WaveEvent,
  VisualFrameContext,
  VisualMutation,
  VisualRenderer,
  VisualSignalEvent,
} from '../../visual/index.js';
import { INSTANCE_FLOATS, InstancedField, NOISE_GLSL, blendingFor } from './materials/InstancedField.js';

/**
 * Smoke.
 *
 * The thing that separates smoke from "some translucent sprites" is that the
 * puffs are not independent. They are carried by one shared flow field, so
 * neighbours move together, fold into each other and shear apart — the eye
 * reads a *volume* moving rather than a hundred objects each doing its own
 * thing. That field is curl noise, which is divergence-free: it swirls and
 * never sprays outward from a point, which is exactly the difference between
 * smoke and a firework.
 *
 * Three things then make it look like a substance rather than a texture:
 *
 *   - **Advection.** Each puff samples the field at its own position and time,
 *     so the field itself drifts and the smoke drifts through it.
 *   - **Growth with dissipation.** A puff expands as it ages and thins as it
 *     expands, because the same amount of stuff is spread over more area. That
 *     relationship, rather than an opacity curve, is what reads as dissipating.
 *   - **Internal structure.** Each puff's fragment shader is fbm-warped, so an
 *     individual puff has cloudy detail and a stack of them has depth. A flat
 *     radial gradient stays visibly a sprite however many you draw.
 *
 * Vorticity is applied as impulses rather than as a constant: a beat injects
 * rotation around a point, and the puffs near it curl for a while afterwards.
 */

/**
 * Smoke's own vertex shader: the quad is stretched along its travel.
 *
 * The default billboard draws a circle, and a hundred circles read as bubbles
 * however cloudy each one is — that is exactly what the first render of this
 * looked like. Real smoke is sheared by the flow carrying it, so each puff is
 * elongated along its velocity and thinned across it. `aShape.w` carries that
 * stretch and `aTransform.w` is the direction of travel rather than a spin.
 */
const SMOKE_VERTEX = /* glsl */ `
attribute vec4 aTransform;  // x, y, scale, heading
attribute vec4 aLife;       // born, lifetime, seed, kind
attribute vec4 aColor;
attribute vec4 aShape;      // warp, churn, ragged, stretch

uniform float uTime;

varying vec2 vUv;
varying vec4 vLife;
varying vec4 vColor;
varying vec4 vShape;
varying float vAge;

void main() {
  vUv = uv;
  vLife = aLife;
  vColor = aColor;
  vShape = aShape;

  float lifetime = max(aLife.y, 0.0001);
  vAge = clamp((uTime - aLife.x) / lifetime, 0.0, 1.0);

  float alive = 1.0 - step(aTransform.z, 0.0);
  float stretch = max(aShape.w, 0.35);

  // Anisotropic: longer along the heading, narrower across it. Area is roughly
  // preserved so a fast puff thins rather than simply growing.
  vec2 local = vec2(position.x * stretch, position.y / sqrt(stretch));
  local *= aTransform.z * alive;

  float c = cos(aTransform.w);
  float s = sin(aTransform.w);
  vec2 rotated = vec2(local.x * c - local.y * s, local.x * s + local.y * c);

  gl_Position = projectionMatrix * modelViewMatrix * vec4(aTransform.xy + rotated, 0.0, 1.0);
}
`;

const SMOKE_FRAGMENT = /* glsl */ `
precision highp float;

${NOISE_GLSL}

uniform float uTime;
uniform float uOpacity;
uniform float uDetail;      // octaves, from the quality preset
uniform float uSoftness;

varying vec2 vUv;
varying vec4 vLife;
varying vec4 vColor;
varying vec4 vShape;
varying float vAge;

void main() {
  vec2 centred = vUv - 0.5;
  float radius = length(centred) * 2.0;
  if (radius > 1.0) discard;

  float seed = vLife.z;
  // The puff's own turbulence, drifting slowly so it churns rather than sits.
  vec2 warp = centred * vShape.x + vec2(seed * 31.7, seed * 17.3) + uTime * 0.06 * vShape.y;
  float body = fbm(warp * 3.4, int(uDetail));

  // Warping the radius by the noise is what gives a ragged, folded edge; a
  // clean radial falloff is what makes a sprite look like a sprite.
  float ragged = radius + (body - 0.5) * 0.85 * vShape.z;

  // Soft, wide falloff: smoke has no boundary, only a thinning.
  //
  // Squaring this once looked right in isolation and measured at a tenth of the
  // frame's luminance against black — a puff whose edge and centre are both
  // nearly transparent is a smudge, not a volume. The falloff is shaped gently
  // instead, and the *stack* of overlapping puffs supplies the depth.
  float density = 1.0 - smoothstep(0.0, uSoftness, ragged);
  density = pow(density, 1.35);

  // Older puffs are larger and correspondingly thinner — the same material
  // spread over more area, which is what dissipation actually is. Held up for
  // most of the life and dropped late, so a puff reads as present and then
  // gone rather than as faint throughout.
  float dissipation = 1.0 - vAge;
  density *= mix(0.45, 1.0, dissipation);

  // A little internal shading so a stack of puffs has depth rather than
  // reading as one flat wash.
  float shade = 0.65 + 0.5 * body;

  float alpha = density * vColor.a * uOpacity;
  if (alpha < 0.002) discard;
  gl_FragColor = vec4(vColor.rgb * shade, alpha);
}
`;

/** One puff of smoke. */
interface Puff {
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  born: number;
  lifetime: number;
  radius: number;
  growth: number;
  seed: number;
  rotation: number;
  spin: number;
  opacity: number;
  r: number;
  g: number;
  b: number;
  /** Shader detail: warp scale, churn rate, raggedness, spare. */
  warp: number;
  churn: number;
  ragged: number;
}

/** A rotational impulse injected into the flow, decaying over time. */
interface Vortex {
  x: number;
  y: number;
  strength: number;
  radius: number;
}

export interface SmokeRendererOptions {
  readonly scene: Scene;
  readonly layerId: string;
  readonly fieldRadius: number;
  readonly capacity?: number;
}

export class SmokeRenderer implements VisualRenderer {
  readonly id = 'SMOKE';

  private readonly scene: Scene;
  private readonly fieldRadius: number;
  private readonly baseCapacity: number;
  private capacity: number;
  private detail = 3;
  private softness = 1.05;

  private field: InstancedField | null = null;
  private puffs: Puff[] = [];
  private readonly vortices: Vortex[] = [];
  private enabled = true;
  private emitAccumulator = 0;

  /** Flow state, driven by signals and mutations rather than set constants. */
  private flowAngle = 0;
  private flowForce = 0.35;
  private turbulence = 0.5;
  private dissipation = 1;
  private frozen = 0;
  private densityBias = 1;

  private pending: {
    strength: number;
    kind: 'plume' | 'burst' | 'lateral';
    /** Field units. Absent means "wherever this renderer likes". */
    at?: { x: number; y: number };
    /** An initial velocity, in field units per second. */
    push?: { x: number; y: number };
    /** A wave's own stream, so its puffs do not depend on the layer's. */
    stream?: VisualRandom;
    scale?: number;
  }[] = [];
  private wavesDrawn = 0;
  private readonly mutationCounts: Record<string, number> = {};
  private lastTime = 0;

  /**
   * The flow, published for anything that wants to be carried by it.
   *
   * Smoke has no coverage to report — it is not something you can be *on* —
   * but it does have a direction at every point, and that is exactly what a
   * text cluster needs in order to drift as though it were inside the plume
   * rather than merely drawn over it. See `SharedVisualField`.
   */
  readonly fieldReader: VisualFieldReader;

  constructor(private readonly options: SmokeRendererOptions) {
    this.scene = options.scene;
    this.fieldRadius = options.fieldRadius;
    this.baseCapacity = options.capacity ?? 160;
    this.capacity = this.baseCapacity;

    const self = this;
    this.fieldReader = {
      sample(x: number, y: number): VisualFieldSample {
        return self.sampleFlow(x, y);
      },
      get coverage(): number {
        return 0;
      },
      get wetness(): number {
        return 0;
      },
    };
  }

  /**
   * Which way this point is being carried, in field units per second.
   *
   * The same two terms the puffs themselves are advected by — the prevailing
   * flow and the curl of the noise — sampled at an arbitrary point rather
   * than at a puff. Computed rather than stored, because storing it would
   * mean a grid that has to be kept in step with a field that is defined
   * everywhere.
   */
  private sampleFlow(x: number, y: number): VisualFieldSample {
    const worldX = x * this.fieldRadius;
    const worldY = y * this.fieldRadius;
    const curl = curlAt(worldX * 0.35, worldY * 0.35, this.lastTime * 0.16, this.turbulence);
    const scale = this.fieldRadius * 0.32;
    const flowX = Math.cos(this.flowAngle) * this.flowForce * scale + curl.x * this.turbulence * 0.7;
    const flowY = Math.sin(this.flowAngle) * this.flowForce * scale + curl.y * this.turbulence * 0.7;
    return {
      coverage: 0,
      density: 0,
      wetness: 0,
      pigment: [0, 0, 0],
      // Back into field units, which is what everything else here speaks.
      flow: [flowX / this.fieldRadius, flowY / this.fieldRadius],
    };
  }

  setQuality(quality: QualityContext): void {
    // Capacity only moves before the pool exists; detail can change any time.
    if (this.field === null) {
      this.capacity = Math.max(16, Math.round(this.baseCapacity * quality.capacityScale));
    }
    this.detail = Math.max(2, Math.min(6, Math.round(2 + quality.iterationScale * 1.6)));
    // Higher quality resolves a softer, wider falloff without banding.
    this.softness = quality.preset === 'LIVE' ? 0.95 : quality.preset === 'HIGH' ? 1.1 : 1.25;
    this.field?.setUniform('uDetail', this.detail);
    this.field?.setUniform('uSoftness', this.softness);
  }

  initialize(context: VisualFrameContext): void {
    if (this.field === null) {
      this.field = new InstancedField({
        scene: this.scene,
        name: `visual:SMOKE:${this.options.layerId}`,
        capacity: this.capacity,
        fragmentShader: SMOKE_FRAGMENT,
        vertexShader: SMOKE_VERTEX,
        blending: NormalBlending,
        renderOrder: 8,
        uniforms: {
          uOpacity: { value: 1 },
          uDetail: { value: this.detail },
          uSoftness: { value: this.softness },
        },
      });
      this.puffs = Array.from({ length: this.capacity }, () => emptyPuff());
    }
    this.field.attach();
    this.lastTime = context.time;
  }

  reset(): void {
    for (const puff of this.puffs) puff.active = false;
    this.vortices.length = 0;
    this.pending.length = 0;
    this.field?.clearAll();
    this.flowAngle = 0;
    this.flowForce = 0.35;
    this.turbulence = 0.5;
    this.dissipation = 1;
    this.frozen = 0;
    this.densityBias = 1;
    for (const key of Object.keys(this.mutationCounts)) delete this.mutationCounts[key];
  }

  handleSignal(event: VisualSignalEvent): void {
    if (event.name === 'ONSET') this.queue(event.strength * 0.7, 'plume');
  }

  applyMutation(item: VisualMutation, context: VisualFrameContext): void {
    this.mutationCounts[item.action] = (this.mutationCounts[item.action] ?? 0) + 1;
    const struck = !item.sustained;
    const force = item.sustained ? item.strength * context.deltaTime : item.strength;

    switch (item.action) {
      case 'SPAWN':
        // A hit is a coherent plume: one push, one direction.
        if (struck) this.queue(item.strength, 'plume');
        break;
      case 'SPLASH':
      case 'BREAK':
        // A miss throws the volume apart rather than dimming it.
        if (struck) {
          for (let index = 0; index < 4; index += 1) this.queue(item.strength, 'burst');
          this.addVortex(context, item.strength * 2.4);
        }
        break;
      case 'SHIFT':
        if (struck) this.queue(item.strength, 'lateral');
        break;
      case 'ROTATE':
        if (struck) this.addVortex(context, item.strength * 1.6);
        else this.flowAngle += force * 0.9;
        break;
      case 'TURBULENCE':
        this.turbulence = Math.min(3, this.turbulence + force * 0.8);
        break;
      case 'DISTORT':
        this.turbulence = Math.min(3, this.turbulence + force * 0.5);
        if (struck) this.addVortex(context, item.strength * 1.2);
        break;
      case 'DENSITY':
      case 'EXPAND':
        this.densityBias = Math.min(2.5, this.densityBias + force * 0.5);
        break;
      case 'CONTRACT':
      case 'FADE':
        this.densityBias = Math.max(0.2, this.densityBias - force * 0.5);
        break;
      case 'FREEZE':
        this.frozen = Math.min(1, this.frozen + force * 0.6);
        break;
      case 'RELEASE':
        this.frozen = Math.max(0, this.frozen - force * 0.6);
        this.dissipation = Math.min(3, this.dissipation + force);
        break;
      default:
        break;
    }
  }

  update(context: VisualFrameContext): void {
    const field = this.field;
    if (field === null || !this.enabled) return;

    const delta = Math.min(0.1, Math.max(0, context.time - this.lastTime));
    this.lastTime = context.time;
    const signals = context.signals;
    const parameters = context.layer.parameters;

    // --- flow state from the music -----------------------------------------
    // Mid steers, bass drives, treble roughens. Smoothed rather than set, so
    // the volume has inertia and does not snap when a band jumps.
    const targetAngle = signals.MID * Math.PI * 2 + numeric(parameters.flowAngle, 0);
    this.flowAngle += angleDelta(this.flowAngle, targetAngle) * Math.min(1, delta * 1.2);
    this.flowForce += (0.25 + signals.BASS * 1.1 - this.flowForce) * Math.min(1, delta * 2);
    this.turbulence += (0.35 + signals.TREBLE * 1.8 - this.turbulence) * Math.min(1, delta * 1.5);
    this.densityBias += (1 - this.densityBias) * delta * 0.4;
    this.frozen = Math.max(0, this.frozen - delta * 0.25);

    // Ambient emission tracks loudness; a silent passage produces almost none.
    const emission = numeric(parameters.emission, 6) * (0.15 + signals.AMPLITUDE * 2.2);
    this.emitAccumulator += emission * delta * (1 - this.frozen);
    while (this.emitAccumulator >= 1) {
      this.emitAccumulator -= 1;
      this.spawn(context, { strength: 0.5, kind: 'ambient' });
    }
    for (const request of this.pending) this.spawn(context, request);
    this.pending.length = 0;

    // Vortices decay; a beat's swirl should be a gesture, not a permanent eddy.
    for (let index = this.vortices.length - 1; index >= 0; index -= 1) {
      const vortex = this.vortices[index] as Vortex;
      vortex.strength *= Math.exp(-delta * 1.1);
      if (Math.abs(vortex.strength) < 0.02) this.vortices.splice(index, 1);
    }

    // --- advection ----------------------------------------------------------
    // Scaled to the field. These were absolute, and the field is several world
    // units across, so the prevailing flow accelerated a puff by a fraction of a
    // percent of the frame per second: the smoke churned in place and never went
    // anywhere. Drift is not a detail of this material — a plume that does not
    // travel is a cloud.
    const flowScale = this.fieldRadius * 0.32;
    const flowX = Math.cos(this.flowAngle) * this.flowForce * flowScale;
    const flowY =
      (Math.sin(this.flowAngle) * this.flowForce + numeric(parameters.rise, 0.35)) * flowScale;
    const step = delta * (1 - this.frozen * 0.9);
    const chaos = context.art.chaos;

    let live = 0;
    for (const puff of this.puffs) {
      if (!puff.active) continue;
      const age = (context.time - puff.born) / puff.lifetime;
      if (age >= 1) {
        puff.active = false;
        continue;
      }

      // Curl of a scalar noise field: divergence-free, so the flow folds rather
      // than expanding away from wherever the sample happens to be.
      const curl = curlAt(puff.x * 0.35, puff.y * 0.35, context.time * 0.16, this.turbulence);
      // The curl is what shears one part of the plume past another, so it is
      // scaled to the field for the same reason the flow is.
      const shear = this.turbulence * 1.4 * this.fieldRadius * 0.5;
      puff.vx += (flowX + curl.x * shear) * step;
      puff.vy += (flowY + curl.y * shear) * step;

      for (const vortex of this.vortices) {
        const dx = puff.x - vortex.x;
        const dy = puff.y - vortex.y;
        const distance = Math.hypot(dx, dy) + 0.001;
        if (distance > vortex.radius) continue;
        const falloff = 1 - distance / vortex.radius;
        // Perpendicular: this is what makes a puff orbit a point rather than
        // being pushed away from it.
        puff.vx += (-dy / distance) * vortex.strength * falloff * step * 6;
        puff.vy += (dx / distance) * vortex.strength * falloff * step * 6;
      }

      // Drag, so a puff settles into the flow instead of accelerating forever.
      const drag = Math.exp(-step * 1.4);
      puff.vx *= drag;
      puff.vy *= drag;
      puff.x += puff.vx * step;
      puff.y += puff.vy * step;
      puff.rotation += puff.spin * step;

      // Growth and thinning are the same process seen twice.
      puff.radius += puff.growth * step * this.dissipation;

      // Small puffs are thin puffs. Without this a freshly spawned one is a
      // tiny disc at full opacity, which reads as a spark rather than as smoke
      // — visible in the first render as bright dots among the wisps.
      const bulk = Math.min(1, puff.radius / (this.fieldRadius * 0.16));
      const fade = 1 - age;
      const alpha = puff.opacity * fade * fade * bulk * this.densityBias *
        context.layer.opacity * context.layer.intensity;

      // Heading and stretch come from the velocity, so a puff being carried
      // fast is drawn long and thin along its travel and a settling one relaxes
      // back towards round.
      const speed = Math.hypot(puff.vx, puff.vy);
      const heading = speed > 0.001 ? Math.atan2(puff.vy, puff.vx) : puff.rotation;
      const stretch = 1 + Math.min(2.2, speed / (this.fieldRadius * 0.28));

      field.write(
        live,
        [puff.x, puff.y, puff.radius, heading],
        [puff.born, puff.lifetime, puff.seed, 0],
        [puff.r, puff.g, puff.b, clamp01(alpha)],
        [puff.warp, puff.churn, puff.ragged * (1 + chaos * 0.8), stretch],
      );
      live += 1;
      if (live >= field.capacity) break;
    }

    field.setTime(context.time);
    field.setUniform('uOpacity', 1);
    field.commit(live);
  }

  /**
   * Adds one puff.
   *
   * The three kinds are three *behaviours*, not three colours. A plume is
   * launched along the flow and holds together; a burst is thrown outward fast
   * and dies young; a lateral drifts across the flow, which is what makes an
   * off-beat press read as a disturbance rather than as more of the same.
   */
  private spawn(
    context: VisualFrameContext,
    request: {
      strength: number;
      kind: string;
      at?: { x: number; y: number };
      push?: { x: number; y: number };
      stream?: VisualRandom;
      scale?: number;
    },
  ): void {
    const field = this.field;
    if (field === null) return;
    const puff = this.puffs.find((candidate) => !candidate.active);
    if (puff === undefined) return;
    const { strength, kind } = request;
    // A wave brings its own stream, so the same event throws the same ring
    // however much work this layer had already done when it arrived.
    const random = request.stream ?? context.random;
    const parameters = context.layer.parameters;

    const spread = this.fieldRadius * numeric(parameters.spread, 0.42);
    puff.active = true;
    puff.born = context.time;
    puff.seed = random.next() * 100;
    puff.rotation = random.range(0, Math.PI * 2);
    puff.spin = random.signed() * 0.4;
    puff.warp = random.range(0.8, 1.8);
    puff.churn = random.range(0.6, 1.6);
    puff.ragged = random.range(0.5, 1.1);

    const baseHue = numeric(parameters.hue, 0.58);
    // Atmospheric rather than neon: low saturation, so this is a lit grey with
    // a cast rather than a colour. Light enough to read against a near-black
    // sky — measured at 0.62 the whole layer averaged a luminance of nine.
    // 0.78 read as cloud rather than smoke — steam-white against the sky. Smoke
    // is a *grey*, lit from nowhere in particular.
    const tint = hslToRgb((baseHue + random.range(-0.03, 0.03) + 1) % 1, 0.12, 0.6);
    puff.r = tint[0];
    puff.g = tint[1];
    puff.b = tint[2];

    switch (kind) {
      case 'burst':
        puff.x = random.signed() * spread * 0.5;
        puff.y = random.signed() * spread * 0.5;
        {
          const angle = random.range(0, Math.PI * 2);
          const speed = this.fieldRadius * random.range(0.5, 1.1) * (0.5 + strength);
          puff.vx = Math.cos(angle) * speed;
          puff.vy = Math.sin(angle) * speed;
        }
        puff.lifetime = random.range(1.8, 3.2);
        puff.radius = this.fieldRadius * random.range(0.05, 0.1);
        puff.growth = this.fieldRadius * random.range(0.32, 0.5);
        puff.opacity = 0.5 + strength * 0.35;
        break;
      case 'lateral':
        // Across the flow, not along it.
        puff.x = random.signed() * spread;
        puff.y = random.signed() * spread * 0.4;
        {
          const across = this.flowAngle + Math.PI / 2;
          const speed = this.fieldRadius * random.range(0.25, 0.5);
          puff.vx = Math.cos(across) * speed * (random.bool() ? 1 : -1);
          puff.vy = Math.sin(across) * speed;
        }
        puff.lifetime = random.range(3.5, 5.8);
        puff.radius = this.fieldRadius * random.range(0.07, 0.13);
        puff.growth = this.fieldRadius * random.range(0.14, 0.24);
        puff.opacity = 0.4 + strength * 0.25;
        break;
      case 'plume':
        puff.x = random.signed() * spread * 0.35;
        puff.y = random.signed() * spread * 0.35 - this.fieldRadius * 0.15;
        puff.vx = Math.cos(this.flowAngle) * this.fieldRadius * 0.25;
        puff.vy = Math.sin(this.flowAngle) * this.fieldRadius * 0.25 + this.fieldRadius * 0.2;
        puff.lifetime = random.range(3.9, 6.4);
        puff.radius = this.fieldRadius * random.range(0.08, 0.14);
        puff.growth = this.fieldRadius * random.range(0.16, 0.26);
        puff.opacity = 0.3 + strength * 0.28;
        break;
      default:
        puff.x = random.signed() * spread;
        puff.y = random.signed() * spread - this.fieldRadius * 0.1;
        puff.vx = random.signed() * 0.2;
        puff.vy = random.range(0.05, 0.3);
        puff.lifetime = random.range(4.5, 7.4);
        puff.radius = this.fieldRadius * random.range(0.09, 0.16);
        puff.growth = this.fieldRadius * random.range(0.12, 0.2);
        puff.opacity = 0.34 + strength * 0.2;
        break;
    }

    // A wave says where and which way; the kind above said what sort of puff.
    // Applied afterwards rather than inside each branch so that adding a kind
    // cannot quietly forget to honour a placement.
    if (request.at !== undefined) {
      // Field units in, world units out.
      puff.x = request.at.x * this.fieldRadius;
      puff.y = request.at.y * this.fieldRadius;
    }
    if (request.push !== undefined) {
      puff.vx = request.push.x * this.fieldRadius;
      puff.vy = request.push.y * this.fieldRadius;
    }
    if (request.scale !== undefined) {
      puff.radius *= request.scale;
      puff.growth *= request.scale;
    }
  }

  private queue(strength: number, kind: 'plume' | 'burst' | 'lateral'): void {
    if (this.pending.length > 24) return;
    this.pending.push({ strength, kind });
  }

  /**
   * A wave, as smoke.
   *
   * Two things at once, because that is what a disturbance in air actually
   * leaves: a **ring** thrown outward from the point in every direction, and a
   * **plume** carried along the way it was travelling. The ring is what makes
   * the event legible as an origin; the plume is what makes the direction
   * legible. Everything after that is the field the puffs were already
   * subject to — they shear, fold and thin exactly as the ambient smoke does,
   * so the wave dissolves into the same material rather than sitting on top
   * of it.
   *
   * A vortex is left at the origin, so the ring rotates as it expands and the
   * fragments do not simply travel outward in a straight line.
   */
  spawnWave(event: WaveEvent): void {
    const stream = new VisualRandom(event.seed);
    const force = clamp01(event.strength) * event.scale;
    const count = event.style === 'torn' ? 9 : event.style === 'stray' ? 3 : 6;

    // The ring.
    for (let index = 0; index < count; index += 1) {
      const angle = (index / count) * Math.PI * 2 + stream.signed() * 0.25;
      const speed = (0.35 + force * 0.5) * event.scale;
      this.pending.push({
        strength: force,
        kind: 'burst',
        at: { x: event.x, y: event.y },
        push: { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
        stream,
        scale: event.scale,
      });
    }

    // The plume, along the direction of travel.
    for (let index = 0; index < 3; index += 1) {
      const spread = event.direction + stream.signed() * 0.35;
      const speed = (0.5 + force * 0.7) * event.scale;
      this.pending.push({
        strength: force,
        kind: 'plume',
        at: {
          x: event.x + Math.cos(event.direction) * 0.05 * index,
          y: event.y + Math.sin(event.direction) * 0.05 * index,
        },
        push: { x: Math.cos(spread) * speed, y: Math.sin(spread) * speed },
        stream,
        scale: event.scale,
      });
    }

    // The rotation it leaves behind.
    this.vortices.push({
      x: event.x * this.fieldRadius,
      y: event.y * this.fieldRadius,
      radius: this.fieldRadius * (0.22 + force * 0.2),
      strength: (event.style === 'torn' ? -1 : 1) * (0.5 + force * 0.8),
    });
    while (this.vortices.length > 8) this.vortices.shift();
    this.wavesDrawn += 1;
  }

  private addVortex(context: VisualFrameContext, strength: number): void {
    if (this.vortices.length > 8) this.vortices.shift();
    const random = context.random;
    this.vortices.push({
      x: random.signed() * this.fieldRadius * 0.5,
      y: random.signed() * this.fieldRadius * 0.5,
      strength: strength * (random.bool() ? 1 : -1),
      radius: this.fieldRadius * random.range(0.4, 0.9),
    });
  }

  render(): void {
    // Drawn by the stage's single render, with everything else in the scene.
  }

  setParameters(parameters: Readonly<Record<string, number | string | boolean>>): void {
    const blend = parameters.blendMode;
    if (typeof blend === 'string' && this.field !== null) {
      this.field.material.blending = blendingFor(blend);
      this.field.material.needsUpdate = true;
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (this.field !== null) this.field.visible = enabled;
    if (!enabled) this.reset();
  }

  dispose(): void {
    this.field?.dispose();
    this.field = null;
    this.puffs = [];
    this.vortices.length = 0;
  }

  /** For diagnostics and the visual checks. */
  get metrics(): {
    readonly live: number;
    readonly capacity: number;
    readonly vortices: number;
    readonly turbulence: number;
    readonly flowForce: number;
    /** Mean puff radius in field radii: how far growth has got. */
    readonly meanRadius: number;
    /**
     * Mean distance of a live puff from the plume's own centre, in field radii.
     *
     * The number that proves the smoke went somewhere. It sat at nearly zero
     * while the flow was expressed in absolute units against a field several
     * world units across, and a plume that churns without travelling is a cloud.
     */
    readonly spread: number;
    readonly waves: number;
    readonly detail: number;
    readonly instanceFloats: number;
    readonly mutations: Readonly<Record<string, number>>;
  } {
    let radius = 0;
    let cx = 0;
    let cy = 0;
    let counted = 0;
    for (const puff of this.puffs) {
      if (!puff.active) continue;
      radius += puff.radius;
      cx += puff.x;
      cy += puff.y;
      counted += 1;
    }
    let spread = 0;
    if (counted > 0) {
      cx /= counted;
      cy /= counted;
      for (const puff of this.puffs) {
        if (!puff.active) continue;
        spread += Math.hypot(puff.x - cx, puff.y - cy);
      }
      spread /= counted;
    }
    return {
      live: this.field?.liveCount ?? 0,
      capacity: this.capacity,
      vortices: this.vortices.length,
      meanRadius: Number((counted === 0 ? 0 : radius / counted / this.fieldRadius).toFixed(4)),
      spread: Number((spread / this.fieldRadius).toFixed(4)),
      waves: this.wavesDrawn,
      turbulence: Number(this.turbulence.toFixed(3)),
      flowForce: Number(this.flowForce.toFixed(3)),
      detail: this.detail,
      instanceFloats: INSTANCE_FLOATS,
      mutations: { ...this.mutationCounts },
    };
  }
}

/**
 * Curl of a 2D scalar noise field.
 *
 * The gradient rotated ninety degrees. Because it is a curl it has zero
 * divergence, which is the whole reason smoke made this way folds and swirls
 * instead of streaming outward — the property that separates it from a
 * particle emitter with noise added.
 */
function curlAt(x: number, y: number, time: number, scale: number): { x: number; y: number } {
  const epsilon = 0.12;
  const frequency = 0.9 + scale * 0.25;
  const at = (px: number, py: number): number =>
    noise2(px * frequency, py * frequency + time) +
    0.5 * noise2(px * frequency * 2.1 + 7.3, py * frequency * 2.1 - time * 0.7);
  const dx = (at(x, y + epsilon) - at(x, y - epsilon)) / (2 * epsilon);
  const dy = (at(x + epsilon, y) - at(x - epsilon, y)) / (2 * epsilon);
  return { x: dx, y: -dy };
}

function noise2(x: number, y: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const a = hash2(ix, iy);
  const b = hash2(ix + 1, iy);
  const c = hash2(ix, iy + 1);
  const d = hash2(ix + 1, iy + 1);
  return a * (1 - ux) * (1 - uy) + b * ux * (1 - uy) + c * (1 - ux) * uy + d * ux * uy;
}

function hash2(x: number, y: number): number {
  const value = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return value - Math.floor(value);
}

function emptyPuff(): Puff {
  return {
    active: false, x: 0, y: 0, vx: 0, vy: 0, born: 0, lifetime: 1, radius: 0,
    growth: 0, seed: 0, rotation: 0, spin: 0, opacity: 0, r: 1, g: 1, b: 1,
    warp: 1, churn: 1, ragged: 1,
  };
}

function angleDelta(from: number, to: number): number {
  let delta = (to - from) % (Math.PI * 2);
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;
  return delta;
}

export function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number): number => {
    let value = t;
    if (value < 0) value += 1;
    if (value > 1) value -= 1;
    if (value < 1 / 6) return p + (q - p) * 6 * value;
    if (value < 1 / 2) return q;
    if (value < 2 / 3) return p + (q - p) * (2 / 3 - value) * 6;
    return p;
  };
  return [channel(h + 1 / 3), channel(h), channel(h - 1 / 3)];
}

function numeric(value: number | string | boolean | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
